import { all, call, cancelled, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { ApiResponse } from 'apisauce'
import { AnyAction } from 'redux'
import { ApiType } from '../Services/api'
import PaymentActions, {PaymentState, PaymentTypes} from '../Redux/paymentRedux'
import * as paymentTerminalActions from '../lib/payment-terminal/actions'
import * as paymentTerminalActionTypes from '../lib/payment-terminal/actionTypes'
import * as paymentTerminalRequests from '../lib/payment-terminal/poplatek/requests'
import {CardPaymentType} from '../Components/SelfService/Types'
import {Transaction, TransactionStatus, ResponseCode, TransactionType} from '../lib/payment-terminal/data/Transaction'
import {isNexiTransactionFinished} from '../lib/payment-terminal/nexi/nexiStatus'
import * as nexiApi from '../lib/payment-terminal/nexi/nexiApi'

const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(() => resolve(undefined), ms))

function createCheckRequest(cardPayment?: CardPaymentType | null) {
    const amount = cardPayment?.amount?.value ? +(cardPayment?.amount?.value) : 0

    if (amount) {
        const checkTransaction = new Transaction({
            id: cardPayment.sequenceId,
            amount: amount,
            transactionType: amount < 0 ? TransactionType.REFUND : TransactionType.PURCHASE,
            currency: 'EUR',
        })

        return paymentTerminalRequests.check(cardPayment.receiptId, checkTransaction);
    }

    return null
}

export function* createPayment(api: ApiType, action: AnyAction) {
    const { token, quick } = action
    const response: ApiResponse<any> = yield call(api.createPayment, { token, quick })
    if (response.ok) {
        const { data } = response
        top.location.href = data.href
    } else {
        top.location.href = '/quick-payment/thank-you/cancel?token=' + token
    }
}


export function* createCardPayment(api: ApiType, action: AnyAction) {
    yield delay(200); // avoid duplicate requests in dev env

    const nexiTerminalId = yield select(state => state.ui.settings.selfService.nexiTerminalId)

    if (nexiTerminalId) {
        yield call(createNexiCardPayment, api, action)
        return
    }

    const terminalState = yield select(state => state.paymentTerminal)

    if (!terminalState.connection.connected && !terminalState.connection.connecting) {
        yield put(paymentTerminalActions.connect())
    }

    const createResult: ApiResponse<any> = yield call(api.postCreateCardPayment, action.token)

    if (createResult.ok) {
        const cardPayment = createResult.data as CardPaymentType

        yield put(PaymentActions.createCardPaymentSuccess(cardPayment, action.token))

        if (cardPayment.isResumed) {
            const checkRequest = createCheckRequest(cardPayment)
            if (checkRequest) {
                yield put(paymentTerminalActions.sendRequest(checkRequest))
            }
        } else {
            yield put(paymentTerminalActions.sendRequest(
                paymentTerminalRequests.purchase(
                    cardPayment.receiptId,
                    new Transaction({
                        amount: cardPayment.amount.value,
                        currency: 'EUR',
                        id: cardPayment.sequenceId,
                        transactionType: TransactionType.PURCHASE,
                    }),
                    {cashier_language: action.language ?? 'fi'}
                )
            ))
        }
    } else {
        yield put(PaymentActions.createCardPaymentFailure())
    }
}

export function* createNexiCardPayment(api: ApiType, action: AnyAction) {
    const jwtResult: ApiResponse<any> = yield call(api.getNexiJwt, action.token)

    if (!jwtResult.ok) {
        yield put(PaymentActions.createCardPaymentFailure())
        return
    }

    const nexiJwt: string = jwtResult.data.token

    const createResult: ApiResponse<any> = yield call(api.postCreateNexiCardPayment, action.token)

    if (!createResult.ok) {
        yield put(PaymentActions.createCardPaymentFailure())
        return
    }

    const cardPayment = createResult.data as CardPaymentType
    yield put(PaymentActions.createCardPaymentSuccess(cardPayment, action.token))

    const nexiTerminalApiUrl = yield select(state => state.ui.settings.selfService.nexiTerminalApiUrl)

    if (cardPayment.isResumed) {
        yield call(pollNexiTransaction, api, cardPayment, nexiTerminalApiUrl, nexiJwt)
        return
    }

    let transaction: Record<string, any> | null
    try {
        transaction = yield call(
            nexiApi.createPayment(nexiTerminalApiUrl, nexiJwt),
            cardPayment.terminalEntityId,
            cardPayment.id,
            cardPayment.amount.value,
            'EUR',
        )
    } catch {
        yield put(PaymentActions.createCardPaymentFailure())
        return
    }

    if (!transaction) {
        yield put(PaymentActions.createCardPaymentFailure())
        return
    }

    yield call(pollNexiTransaction, api, { ...cardPayment, sequenceId: transaction.id }, nexiTerminalApiUrl, nexiJwt)
}

function* pollNexiTransaction(api: ApiType, cardPayment: CardPaymentType, nexiTerminalApiUrl: string, nexiJwt: string) {
    const { terminalEntityId, sequenceId } = cardPayment

    try {
        while (true) {
            yield call(delay, 3000)

            const isAborting = yield select(state => state.payment.cardPaymentStatus?.isAborting)

            if (isAborting) {
                yield call(nexiApi.abortPayment(nexiTerminalApiUrl, nexiJwt), terminalEntityId, sequenceId)
            }

            let transaction: Record<string, any>
            try {
                transaction = yield call(nexiApi.fetchTransactionStatus(nexiTerminalApiUrl, nexiJwt), terminalEntityId, sequenceId)
            } catch {
                continue
            }

            const status = transaction?.status

            if (isNexiTransactionFinished(status)) {
                const isPaid = (status === TransactionStatus.CLOSED || status === TransactionStatus.CLOSED_NOT_PROCESSED)
                    && transaction?.responseCode === ResponseCode.OK
                const cardToken = yield select(state => state.payment.cardToken)
                const resultResponse: ApiResponse<any> = yield call(api.postNexiCardPaymentResult, cardToken, sequenceId, { isPaid })

                if (resultResponse.ok) {
                    yield put(PaymentActions.cardPaymentCompleted(resultResponse.data))
                } else {
                    yield put(PaymentActions.cardPaymentCompleted({...cardPayment, isPaid: false}))
                }
                return
            }
        }
    } finally {
        if (yield cancelled()) {
            try {
                yield call(nexiApi.abortPayment(nexiTerminalApiUrl, nexiJwt), terminalEntityId, sequenceId)
            } catch {
                //
            }
        }
    }
}

export function* abortCardPayment() {
    const nexiTerminalId = yield select(state => state.ui.settings.selfService.nexiTerminalId)

    if (nexiTerminalId) {
        return
    }

    const terminalState = yield select(state => state.paymentTerminal)

    if (terminalState.connection.connected) {
        yield put(paymentTerminalActions.sendRequest(paymentTerminalRequests.abort()))
    }
}

export function* handleCardPaymentResult(api: ApiType, action: AnyAction) {
    const transaction: Transaction = action.result
    const cardPayment = yield select(state => state.payment.cardPayment)
    const cardToken = yield select(state => state.payment.cardToken)
    const cardPaymentResult = {...cardPayment, transaction: transaction.terminalResultJson ?? JSON.stringify(transaction)}

    if (cardToken) {
        const response = yield call(api.postCardPaymentResult, cardToken, cardPaymentResult)

        if (response.ok) {
            yield put(PaymentActions.cardPaymentCompleted(response.data))
        } else {
            yield put(PaymentActions.cardPaymentCompleted(cardPaymentResult))
        }

        yield put(paymentTerminalActions.disconnect())
    } else {
        yield put(paymentTerminalActions.disconnect())
    }
}

export function* handlePaymentTerminalIdle() {
    const cardPayment = yield select(({payment}: {payment: PaymentState}) => payment.cardPayment)
    const cardPaymentStatus = yield select(({payment}: {payment: PaymentState}) => payment.cardPaymentStatus)
    const terminalState = yield select(state => state.paymentTerminal)

    if (cardPayment?.amount?.value && !cardPaymentStatus.isCompleted) {
        yield put(paymentTerminalActions.sendRequest(createCheckRequest(cardPayment)))
    }
    if (cardPaymentStatus.isAborting && (terminalState.connection.connected || terminalState.connection.connecting)) {
        if (terminalState.status.transactionStatus === null && terminalState.status.readyForTransaction) {
            yield put(paymentTerminalActions.disconnect())
        } else {
            yield put(paymentTerminalActions.sendRequest(paymentTerminalRequests.status()))
            yield put(paymentTerminalActions.sendRequest(paymentTerminalRequests.abort()))
        }
    } else if (terminalState.connection.connected && !terminalState.info.terminalId) {
        yield put(paymentTerminalActions.sendRequest(paymentTerminalRequests.terminalInfo()))
    }
}


export function* paymentSagas(API: ApiType): Generator {
    yield all([
        takeLatest(PaymentTypes.CREATE_PAYMENT, createPayment, API),
        takeLatest(PaymentTypes.CREATE_CARD_PAYMENT, createCardPayment, API),
        takeLatest(PaymentTypes.ABORT_CARD_PAYMENT, abortCardPayment),
        takeLatest(paymentTerminalActionTypes.PAYMENT_TERMINAL_IDLE, handlePaymentTerminalIdle),
        takeEvery(paymentTerminalActionTypes.PAYMENT_TERMINAL_TRANSACTION_COMPLETED, handleCardPaymentResult, API),
    ])
}
